import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type GeneratePlanInput, type WbsTemplateView } from '@apm/contracts';
import { Button } from '../../../components/ui/button';
import { AiAction } from '../../ai/components/ai-action';
import { DatePicker } from '../../../components/ui/date-picker';

/** Ba bước trước khi có nháp. Bước "duyệt" nằm ở `PlanPanel`. */
type Step = 'describe' | 'context';

const MIN_DESCRIPTION = 20;

interface PlanWizardProps {
  readonly projectId: string;
  readonly templates: readonly WbsTemplateView[];
  readonly pending: boolean;
  readonly onGenerate: (input: Omit<GeneratePlanInput, 'projectId'>) => void;
}

/**
 * Wizard lập kế hoạch, bước 1–2 — APM-806.
 *
 * Tách mô tả khỏi bối cảnh vì hai thứ này đến từ hai chỗ khác nhau: mô tả
 * thường được **dán** từ tài liệu, còn mốc thời gian và ràng buộc là thứ người
 * dùng nghĩ ra ngay lúc lập kế hoạch. Trộn vào một ô thì lần sinh lại phải sửa
 * cả đoạn văn, và phần bối cảnh — phần đổi cách chia việc nhiều nhất — lại là
 * phần dễ bị bỏ quên nhất.
 *
 * Bước bối cảnh **không bắt buộc**: bỏ trống thì AI sẽ hỏi lại ở bước sau. Đó
 * là điểm của APM-806 — thiếu thông tin thì hỏi, không tự điền.
 */
export function PlanWizard({
  projectId,
  templates,
  pending,
  onGenerate,
}: PlanWizardProps): ReactNode {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('describe');
  const [description, setDescription] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [deadline, setDeadline] = useState('');
  const [constraints, setConstraints] = useState('');

  const tooShort = description.trim().length < MIN_DESCRIPTION;

  const run = () =>
    onGenerate({
      description: description.trim(),
      ...(templateId ? { templateId } : {}),
      ...(deadline ? { deadline } : {}),
      ...(constraints.trim() ? { constraints: constraints.trim() } : {}),
    });

  return (
    <section className="rounded-DEFAULT border border-border p-3">
      <header className="mb-2 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-medium">{t('plan.generateTitle')}</h3>
        <StepDots current={step} />
      </header>

      {step === 'describe' ? (
        <>
          <textarea
            className="h-24 w-full rounded-control border border-border-strong bg-background p-2 text-sm"
            placeholder={t('plan.descriptionPlaceholder')}
            aria-label={t('plan.description')}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />

          <div className="mt-2 flex flex-wrap items-center gap-2">
            <select
              className="h-8 rounded-control border border-border-strong bg-background px-2 text-xs"
              aria-label={t('plan.template')}
              value={templateId}
              onChange={(event) => setTemplateId(event.target.value)}
            >
              <option value="">{t('plan.noTemplate')}</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>

            <Button size="sm" disabled={tooShort} onClick={() => setStep('context')}>
              {t('plan.next')}
            </Button>

            <span className="text-xs text-muted-foreground">
              {tooShort ? t('plan.needLongerDescription') : ''}
            </span>
          </div>
        </>
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-muted-foreground" htmlFor="plan-deadline">
              {t('plan.deadline')}
            </label>
            <DatePicker
              id="plan-deadline"
              className="w-40"
              value={deadline}
              onChange={setDeadline}
            />
            <span className="text-xs text-muted-foreground">{t('plan.optional')}</span>
          </div>

          <textarea
            className="h-20 w-full rounded-control border border-border-strong bg-background p-2 text-sm"
            placeholder={t('plan.constraintsPlaceholder')}
            aria-label={t('plan.constraints')}
            value={constraints}
            maxLength={2_000}
            onChange={(event) => setConstraints(event.target.value)}
          />

          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setStep('describe')}>
              {t('plan.back')}
            </Button>

            {/* `<AiAction>` lo nhánh "chưa bật AI": nếu chưa có key thì chỗ này
                thành đường dẫn sang backlog để tự tạo task (ADR-006). */}
            <AiAction
              skill="generate_plan"
              label={t('plan.generate')}
              pending={pending}
              onRun={run}
              routeParams={{ projectId }}
            />

            <span className="text-xs text-muted-foreground">{t('plan.contextHint')}</span>
          </div>
        </div>
      )}
    </section>
  );
}

/** Chỉ báo bước. Bước "làm rõ" có thể không xảy ra — AI chỉ hỏi khi thiếu thật. */
function StepDots({ current }: { readonly current: Step }): ReactNode {
  const { t } = useTranslation();
  const steps: readonly { key: string; label: string }[] = [
    { key: 'describe', label: t('plan.step.describe') },
    { key: 'context', label: t('plan.step.context') },
    { key: 'clarify', label: t('plan.step.clarify') },
    { key: 'review', label: t('plan.step.review') },
  ];

  return (
    <ol className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground">
      {steps.map((step, index) => (
        <li key={step.key} className={step.key === current ? 'font-medium text-foreground' : ''}>
          {index > 0 ? <span aria-hidden> › </span> : null}
          {step.label}
        </li>
      ))}
    </ol>
  );
}
